import { useState, useEffect, useRef } from 'react'
import useSettingsStore, { DEFAULT_SETTINGS } from '../store/settingsStore'
import { useCritterStore } from '../store/critterStore'
import { modelApi } from '../services/modelApi'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { ANIMATION_STATE_LABELS, EMPTY_ANIMATION_MAP } from '../constants/animationStates'
import { get3DTypes } from '../utils/critterTypeHelpers'
import { autoMapAnimations } from '../utils/animationAutoMap'
import '../styles/SettingsPanel.css'
import '../styles/Settings3DPanel.css'

const Settings3DPanel = ({ isAdmin = false, broadcasterLogin = null }) => {
  const {
    settings,
    is3DPanelOpen,
    toggle3DPanel,
    close3DPanel,
    updateSetting,
    saveToApi,
    hasChanges,
    isLoading
  } = useSettingsStore()

  const { critters, critterTypes, clearAllCritters } = useCritterStore()
  const threeDTypes = get3DTypes(critterTypes)

  const [activeTab, setActiveTab] = useState('settings')
  const [saveStatus, setSaveStatus] = useState(null)

  // Admin actions
  const [adminUsername, setAdminUsername] = useState('')
  const [adminCritterType, setAdminCritterType] = useState('')

  // Model upload state
  const [selectedCritterType, setSelectedCritterType] = useState('')
  const [modelFile, setModelFile] = useState(null)
  const [modelFileName, setModelFileName] = useState(null)
  const [modelScale, setModelScale] = useState(5.0)
  const [animationMap, setAnimationMap] = useState({ ...EMPTY_ANIMATION_MAP })
  const [discoveredClips, setDiscoveredClips] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [uploadedModels, setUploadedModels] = useState([])
  const [isLoadingModels, setIsLoadingModels] = useState(false)

  const fileRef = useRef(null)

  useEffect(() => {
    if (is3DPanelOpen && broadcasterLogin) {
      loadUploadedModels()
    }
  }, [is3DPanelOpen, broadcasterLogin])

  const loadUploadedModels = async () => {
    setIsLoadingModels(true)
    try {
      modelApi.setBroadcasterLogin(broadcasterLogin)
      const list = await modelApi.getAllModels()
      setUploadedModels(list)
    } catch (err) {
      console.error('Failed to load models:', err)
    } finally {
      setIsLoadingModels(false)
    }
  }

  const handleSave = async () => {
    const result = await saveToApi()
    setSaveStatus(result ? 'saved' : 'error')
    setTimeout(() => setSaveStatus(null), 2000)
  }

  const handleAddCritter = () => {
    if (adminUsername.trim() && adminCritterType) {
      const { addCritter } = useCritterStore.getState()
      addCritter(`admin_${Date.now()}`, adminUsername.trim().toLowerCase(), adminCritterType)
      setAdminUsername('')
    }
  }

  const handleRemoveCritter = (userId) => {
    const { removeCritter } = useCritterStore.getState()
    removeCritter(userId)
  }

  const handleClearAll = () => {
    if (confirm('Are you sure you want to remove all critters?')) {
      clearAllCritters()
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.name.toLowerCase().endsWith('.gltf')) {
      setError('GLTF text format is not supported. Please export as GLB (binary GLTF) instead. GLB is a single self-contained file that includes all mesh data and textures.')
      if (fileRef.current) fileRef.current.value = ''
      return
    }

    setError(null)
    setModelFile(file)
    setModelFileName(file.name)

    const reader = new FileReader()
    reader.onload = (event) => {
      const loader = new GLTFLoader()
      loader.parse(event.target.result, '', (gltf) => {
        const clips = gltf.animations.map((a) => a.name)
        setDiscoveredClips(clips)

        const newMap = autoMapAnimations(clips)
        setAnimationMap(newMap)
        setSuccess(`Found ${clips.length} animation${clips.length !== 1 ? 's' : ''}: ${clips.join(', ') || 'none'}`)
      }, (err) => {
        console.error('Failed to parse model:', err)
        setDiscoveredClips([])
        setError('Failed to parse model file. Make sure it is a valid GLB/GLTF file.')
      })
    }
    reader.readAsArrayBuffer(file)
  }

  const clearFiles = () => {
    setModelFile(null)
    setModelFileName(null)
    setDiscoveredClips([])
    setAnimationMap({ ...EMPTY_ANIMATION_MAP })
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleModelSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!selectedCritterType) {
      setError('Please select a critter type')
      return
    }
    if (!modelFile) {
      setError('Please upload a 3D model file (.glb or .gltf)')
      return
    }

    setIsSubmitting(true)
    try {
      modelApi.setBroadcasterLogin(broadcasterLogin)
      await modelApi.uploadModel(selectedCritterType, modelFile, animationMap, modelScale)
      setSuccess(`Successfully uploaded 3D model for ${selectedCritterType}`)
      clearFiles()

      const { loadCritterTypes } = useCritterStore.getState()
      loadCritterTypes()
      await loadUploadedModels()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteModel = async (critterType) => {
    if (!confirm(`Delete 3D model for ${critterType}?`)) return

    try {
      modelApi.setBroadcasterLogin(broadcasterLogin)
      await modelApi.deleteModel(critterType)

      const { loadCritterTypes } = useCritterStore.getState()
      loadCritterTypes()
      await loadUploadedModels()
    } catch (err) {
      setError(err.message)
    }
  }

  if (!is3DPanelOpen) return null

  return (
    <div className="settings-overlay">
      <div className="settings-panel settings-panel-3d">
        <div className="settings-header">
          <h2>3D Model Settings</h2>
          <button className="close-btn" onClick={close3DPanel}>&times;</button>
        </div>

        <div className="settings-tabs">
          <button
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
          <button
            className={`tab ${activeTab === 'models' ? 'active' : ''}`}
            onClick={() => setActiveTab('models')}
          >
            Upload Models
          </button>
          <button
            className={`tab ${activeTab === 'library' ? 'active' : ''}`}
            onClick={() => setActiveTab('library')}
          >
            Model Library
          </button>
          {isAdmin && (
            <button
              className={`tab ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('admin')}
            >
              Admin
            </button>
          )}
          <button
            className={`tab ${activeTab === 'help' ? 'active' : ''}`}
            onClick={() => setActiveTab('help')}
          >
            Help
          </button>
        </div>

        <div className="settings-content">
          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="settings-section">
              <h3>3D Rendering</h3>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.enable3D}
                    onChange={(e) => updateSetting('enable3D', e.target.checked)}
                  />
                  Enable 3D Models
                </label>
                <span className="setting-hint">Render uploaded 3D models instead of 2D sprites for critter types that have a model.</span>
              </div>

              <div className="setting-item">
                <label>
                  Global 3D Scale
                  <span className="setting-value">{settings.threeDModelScale.toFixed(1)}x</span>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="3.0"
                  step="0.1"
                  value={settings.threeDModelScale}
                  onChange={(e) => updateSetting('threeDModelScale', parseFloat(e.target.value))}
                />
                <span className="setting-hint">Global scale multiplier applied to all 3D models</span>
              </div>

              <h3>3D Movement</h3>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.threeDStaticMode}
                    onChange={(e) => updateSetting('threeDStaticMode', e.target.checked)}
                  />
                  Static Mode
                </label>
                <span className="setting-hint">3D critters spawn at random positions but don't move. Animations still play.</span>
              </div>

              <div className="setting-item">
                <label>
                  Roam Interval
                  <span className="setting-value">{settings.threeDRoamInterval}ms</span>
                </label>
                <input
                  type="range"
                  min="1000"
                  max="10000"
                  step="500"
                  value={settings.threeDRoamInterval}
                  onChange={(e) => updateSetting('threeDRoamInterval', parseInt(e.target.value))}
                />
                <span className="setting-hint">How often 3D critters pick a new destination</span>
              </div>

              <div className="setting-item">
                <label>
                  Movement Speed
                  <span className="setting-value">{settings.threeDRoamSpeed.toFixed(2)}</span>
                </label>
                <input
                  type="range"
                  min="0.05"
                  max="1.0"
                  step="0.05"
                  value={settings.threeDRoamSpeed}
                  onChange={(e) => updateSetting('threeDRoamSpeed', parseFloat(e.target.value))}
                />
                <span className="setting-hint">Speed of 3D critter movement (% per update tick)</span>
              </div>

              <div className="setting-item">
                <label>
                  Update Interval
                  <span className="setting-value">{settings.threeDUpdateInterval}ms</span>
                </label>
                <input
                  type="range"
                  min="50"
                  max="500"
                  step="50"
                  value={settings.threeDUpdateInterval}
                  onChange={(e) => updateSetting('threeDUpdateInterval', parseInt(e.target.value))}
                />
                <span className="setting-hint">Position update frequency. Lower = smoother but more CPU.</span>
              </div>

              <div className="setting-item">
                <label>
                  Smoothing
                  <span className="setting-value">{settings.threeDLerpSpeed}</span>
                </label>
                <input
                  type="range"
                  min="2"
                  max="20"
                  step="1"
                  value={settings.threeDLerpSpeed}
                  onChange={(e) => updateSetting('threeDLerpSpeed', parseInt(e.target.value))}
                />
                <span className="setting-hint">Visual smoothing for 3D model movement. Higher = snappier, lower = gliding.</span>
              </div>

              <h3>3D Critter Types ({Object.keys(threeDTypes).length})</h3>
              <div className="critter-type-grid">
                {Object.entries(threeDTypes).map(([key, type]) => (
                  <div key={key} className={`critter-type-card ${type.renderMode === '3d' ? 'active-3d' : ''}`}>
                    <span className="type-name">{type.name}</span>
                    <span className={`type-badge ${type.renderMode === '3d' ? '' : 'type-badge-pending'}`}>
                      {type.renderMode === '3d' ? 'Ready' : 'No Model'}
                    </span>
                  </div>
                ))}
              </div>
              <span className="setting-hint">Types marked "No Model" need a GLB file uploaded in the Upload Models tab.</span>
            </div>
          )}

          {/* Upload Models Tab */}
          {activeTab === 'models' && (
            <div className="settings-section">
              <h3>Upload 3D Model</h3>

              {error && <div className="error-message panel-message">{error}</div>}
              {success && <div className="success-message panel-message">{success}</div>}

              <form onSubmit={handleModelSubmit}>
                <div className="setting-row">
                  <div className="setting-item half">
                    <label>Critter Type</label>
                    <select
                      value={selectedCritterType}
                      onChange={(e) => setSelectedCritterType(e.target.value)}
                    >
                      <option value="">Select a critter...</option>
                      {Object.entries(threeDTypes).map(([key, type]) => (
                        <option key={key} value={key}>{type.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="setting-item half">
                    <label>
                      Model Scale
                      <span className="setting-value">{modelScale.toFixed(1)}</span>
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="20"
                      step="0.5"
                      value={modelScale}
                      onChange={(e) => setModelScale(parseFloat(e.target.value))}
                    />
                  </div>
                </div>

                <div className="model-upload-zone"
                  onClick={() => fileRef.current?.click()}
                >
                  {modelFileName ? (
                    <div className="model-file-info">
                      <span className="model-icon">🧊</span>
                      <span className="model-name">{modelFileName}</span>
                      <button type="button" className="remove-file-btn" onClick={(e) => { e.stopPropagation(); clearFiles() }}>&times;</button>
                    </div>
                  ) : (
                    <div className="model-upload-prompt">
                      <span className="model-icon">🧊</span>
                      <span>Click to upload GLB model</span>
                      <span className="upload-hint">Max 50MB</span>
                    </div>
                  )}
                </div>
                <input type="file" ref={fileRef} accept=".glb" onChange={handleFileChange} hidden />

                {/* Animation mapping */}
                {discoveredClips.length > 0 && (
                  <div className="animation-mapping-section">
                    <h3>Animation Mapping</h3>
                    <span className="setting-hint">Map your model's animation clips to critter behaviors</span>
                    <div className="animation-map-grid">
                      {Object.entries(ANIMATION_STATE_LABELS).map(([key, label]) => (
                        <div key={key} className="animation-map-row">
                          <span className="animation-label">{label}</span>
                          <select
                            value={animationMap[key] || ''}
                            onChange={(e) => setAnimationMap((prev) => ({ ...prev, [key]: e.target.value }))}
                          >
                            <option value="">-- None --</option>
                            {discoveredClips.map((clip) => (
                              <option key={clip} value={clip}>{clip}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button type="submit" className="save-btn upload-model-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Uploading...' : 'Upload 3D Model'}
                </button>
              </form>
            </div>
          )}

          {/* Model Library Tab */}
          {activeTab === 'library' && (
            <div className="settings-section">
              <h3>Uploaded 3D Models ({uploadedModels.length})</h3>

              {isLoadingModels ? (
                <p className="empty-list">Loading models...</p>
              ) : uploadedModels.length === 0 ? (
                <div className="empty-library">
                  <p className="empty-list">No 3D models uploaded yet.</p>
                  <button
                    className="create-critter-btn"
                    onClick={() => setActiveTab('models')}
                  >
                    Upload Your First Model
                  </button>
                </div>
              ) : (
                <div className="model-library-list">
                  {uploadedModels.map((item) => (
                    <div key={item.critter_type} className="model-library-item">
                      <div className="model-item-info">
                        <span className="model-item-icon">🧊</span>
                        <div className="model-item-details">
                          <span className="model-item-name">
                            {critterTypes[item.critter_type]?.name || item.critter_type}
                          </span>
                          <span className="model-item-meta">
                            Scale: {item.model_scale || 5.0}x
                            {item.animation_map && ` · ${Object.values(item.animation_map).filter(Boolean).length} animations`}
                          </span>
                        </div>
                      </div>
                      <button
                        className="remove-btn"
                        onClick={() => handleDeleteModel(item.critter_type)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Admin Tab */}
          {activeTab === 'admin' && isAdmin && (
            <div className="settings-section">
              <h3>Quick Actions</h3>

              <div className="admin-actions">
                <div className="admin-action">
                  <h4>Add 3D Critter</h4>
                  <div className="admin-form">
                    <input
                      type="text"
                      placeholder="Username"
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                    />
                    <select
                      value={adminCritterType}
                      onChange={(e) => setAdminCritterType(e.target.value)}
                    >
                      <option value="">Select type...</option>
                      {Object.entries(threeDTypes).map(([key, type]) => (
                        <option key={key} value={key}>{type.name}</option>
                      ))}
                    </select>
                    <button onClick={handleAddCritter}>Add</button>
                  </div>
                </div>

                <div className="admin-action">
                  <h4>Remove All Critters</h4>
                  <button className="danger-btn" onClick={handleClearAll}>
                    Clear All Critters
                  </button>
                </div>
              </div>

              <h3>Active 3D Critters</h3>
              <div className="critter-list">
                {(() => {
                  const threeDCritters = Object.values(critters).filter(
                    (c) => threeDTypes[c.critterType]
                  )
                  return threeDCritters.length === 0 ? (
                    <p className="empty-list">No active 3D critters</p>
                  ) : (
                    threeDCritters.map((critter) => (
                      <div key={critter.userId} className="critter-list-item">
                        <span className="critter-info">
                          <strong>{critter.username}</strong>
                          <span className="critter-type">({critter.critterType})</span>
                        </span>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveCritter(critter.userId)}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )
                })()}
              </div>
            </div>
          )}

          {/* Help Tab */}
          {activeTab === 'help' && (
            <div className="settings-section help-section">
              <h3>Quick Reference</h3>
              <div className="help-block">
                <div className="help-command"><code>GLB only</code><span>Only .glb (binary GLTF) files accepted</span></div>
                <div className="help-command"><code>Max 50MB</code><span>Keep under 10MB for best performance</span></div>
                <div className="help-command"><code>14 animations</code><span>Idle A/B/C, Walk, Run, Eat, Pet, Dance, Trick, Hug, Bonk, Sleep, Spawn, Despawn</span></div>
                <div className="help-command"><code>Auto-map</code><span>Clips are mapped by name when detected</span></div>
                <div className="help-command"><code>Fallbacks</code><span>Missing animations fall back to alternatives</span></div>
              </div>

              <div className="help-block" style={{textAlign: 'center', padding: '24px'}}>
                <p style={{marginBottom: '12px'}}>For complete 3D documentation, Unity export guide, and animation mapping details:</p>
                <a
                  href="/guide#3d-overview"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    background: '#10b981',
                    color: 'white',
                    padding: '10px 24px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '14px'
                  }}
                >
                  Open 3D Documentation
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="settings-footer">
          <div className="save-area" style={{ marginLeft: 'auto' }}>
            {saveStatus === 'saved' && <span className="save-success">Saved!</span>}
            {saveStatus === 'error' && <span className="save-error">Error saving</span>}
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={!hasChanges || isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings3DPanel
